import React, { useState, useEffect } from 'react';
import HeaderLayout from '../common/HeaderLayout';
import { useAuth } from './Authcontext';
import { toast, ToastContainer } from 'react-toastify';

const API_URL = 'https://api.rangpursadhima.cloud/api';

// Field name to friendly label mapping
const FIELD_LABELS = {
  // User fields
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  MOBILE_NO: 'Mobile No',
  CONTACT_NO: 'Contact No',
  ADDRESS: 'Address',
  CITY_AREA_VILLAGE: 'City/Area/Village',
  PIN_CODE: 'PIN Code',
  TALUKA: 'Taluka',
  DISTRICT: 'District',
  STATE: 'State',
  COUNTRY: 'Country',
  DOB: 'Date of Birth',
  Sevakcode: 'Sevak Code',
  image_url: 'Profile Image',
  
  // Admin fields
  full_name: 'Full Name',
  email: 'Email',
  mobile_no: 'Mobile No',
  role: 'Role',
  is_active: 'Status',
  password: 'Password',
  
  // Attendance fields
  user_id: 'User ID',
  slot: 'Slot',
  sevakCode: 'Sevak Code',
  userName: 'User Name',
};

// Get friendly label for a field
const getFieldLabel = (fieldName) => {
  return FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Format field value for display
const formatFieldValue = (fieldName, value) => {
  if (value === null || value === undefined || value === '') {
    return '(empty)';
  }
  
  // Handle boolean values
  if (fieldName === 'is_active') {
    return value ? 'Active' : 'Inactive';
  }
  
  // Handle slot values
  if (fieldName === 'slot') {
    if (value === 'Morning_presentation' || value === 'Morning') return '🌅 Morning';
    if (value === 'evening_presentation' || value === 'Evening') return '🌙 Evening';
  }
  
  // Handle dates
  if (fieldName === 'DOB' && value) {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  }
  
  return String(value);
};

const ControlPanel = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('admins');
  const [admins, setAdmins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({});
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    mobile_no: '',
    role: 'ADMIN',
  });

  // Mudat Stats
  const [mudatStats, setMudatStats] = useState({ past: [], upcoming: [] });
  const [selectedMudatDate, setSelectedMudatDate] = useState(null);
  const [mudatDetail, setMudatDetail] = useState([]);

  // Sales
  const [salesData, setSalesData] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesByUser, setSalesByUser] = useState([]);
  const [salesDateFilter, setSalesDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [salesPayFilter, setSalesPayFilter] = useState('');
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesDeletingId, setSalesDeletingId] = useState(null);

  // Filters for logs
  const [logFilters, setLogFilters] = useState({
    userType: '',
    action: '',
    category: '',
    sevakCode: '',
    fromDate: '',
    toDate: '',
    page: 1,
  });

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    } else if (activeTab === 'logs') {
      fetchLogs();
      fetchCategories();
      fetchActions();
    } else if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'mudat') {
      fetchMudatStats();
    } else if (activeTab === 'sales') {
      fetchSalesData(salesDateFilter, salesPayFilter);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [logFilters]);

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAdmins(data);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/logs/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchActions = async () => {
    try {
      const res = await fetch(`${API_URL}/logs/actions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setActions(data);
      }
    } catch (err) {
      console.error('Error fetching actions:', err);
    }
  };

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: logFilters.page,
        limit: 20,
      });
      if (logFilters.userType) params.append('userType', logFilters.userType);
      if (logFilters.action) params.append('action', logFilters.action);
      if (logFilters.category) params.append('category', logFilters.category);
      if (logFilters.sevakCode) params.append('sevakCode', logFilters.sevakCode);
      if (logFilters.fromDate) params.append('fromDate', logFilters.fromDate);
      if (logFilters.toDate) params.append('toDate', logFilters.toDate);

      const res = await fetch(`${API_URL}/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setLogsPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/logs/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMudatStats = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/mudat/sunday-stats`);
      if (res.ok) {
        const data = await res.json();
        setMudatStats(data);
      }
    } catch (err) {
      console.error('Error fetching mudat stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMudatDetail = async (date) => {
    setSelectedMudatDate(date);
    try {
      const res = await fetch(`${API_URL}/mudat/sunday-stats/${date}`);
      if (res.ok) {
        const data = await res.json();
        setMudatDetail(data);
      }
    } catch (err) {
      console.error('Error fetching mudat detail:', err);
    }
  };

  const fetchSalesData = async (date, payMode) => {
    setSalesLoading(true);
    try {
      const d = date || salesDateFilter;
      const params = new URLSearchParams({ limit: 200 });
      if (d) params.append('date', d);
      if (payMode !== undefined ? payMode : salesPayFilter) params.append('payment_mode', payMode !== undefined ? payMode : salesPayFilter);

      const [salesRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/sales?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/sales/summary?date=${d}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (salesRes.ok) {
        const sd = await salesRes.json();
        setSalesData(sd.sales || []);
      }
      if (summaryRes.ok) {
        const sm = await summaryRes.json();
        setSalesSummary(sm.summary);
        setSalesByUser(sm.byUser || []);
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setSalesLoading(false);
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    setSalesDeletingId(saleId);
    try {
      const userStr = localStorage.getItem('user');
      const roleStr = localStorage.getItem('role');
      let actorInfo = { actorId: 0, actorRole: roleStr || 'SUPERADMIN', actorName: 'SuperAdmin' };
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          actorInfo = { actorId: u.id || 0, actorRole: roleStr || 'SUPERADMIN', actorName: u.name || 'SuperAdmin' };
        } catch (e) {}
      }
      const res = await fetch(`${API_URL}/sales/${saleId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(actorInfo),
      });
      if (res.ok) {
        toast.success('Sale deleted!');
        fetchSalesData(salesDateFilter, salesPayFilter);
      } else {
        const err = await res.json();
        toast.error(err.error || 'Delete failed');
      }
    } catch (err) {
      toast.error('Error deleting sale');
    } finally {
      setSalesDeletingId(null);
    }
  };

  // Helper to get the coming Sunday (or today if Sunday)
  const getComingSunday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    return sunday.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const url = editingAdmin
        ? `${API_URL}/admin/${editingAdmin.id}`
        : `${API_URL}/admin`;
      const method = editingAdmin ? 'PUT' : 'POST';

      // Get actor info from localStorage
      const userStr = localStorage.getItem('user');
      const role = localStorage.getItem('role');
      let actorInfo = { actorId: 0, actorRole: role || 'SUPERADMIN', actorEmail: null };
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          actorInfo = {
            actorId: user.id || 0,
            actorRole: role || 'SUPERADMIN',
            actorEmail: user.email || null,
          };
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }

      const body = { ...formData, ...actorInfo };
      if (editingAdmin && !formData.password) {
        delete body.password;
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(editingAdmin ? 'Admin updated!' : 'Admin created!');
        setShowModal(false);
        setEditingAdmin(null);
        setFormData({
          full_name: '',
          email: '',
          password: '',
          mobile_no: '',
          role: 'ADMIN',
        });
        fetchAdmins();
      } else {
        const error = await res.json();
        toast.error(error.error || 'Operation failed');
      }
    } catch (err) {
      toast.error('Error saving admin');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;

    try {
      // Get actor info from localStorage
      const userStr = localStorage.getItem('user');
      const role = localStorage.getItem('role');
      let actorInfo = { actorId: 0, actorRole: role || 'SUPERADMIN', actorEmail: null };
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          actorInfo = {
            actorId: user.id || 0,
            actorRole: role || 'SUPERADMIN',
            actorEmail: user.email || null,
          };
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }

      const res = await fetch(`${API_URL}/admin/${id}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(actorInfo),
      });

      if (res.ok) {
        toast.success('Admin deleted!');
        fetchAdmins();
      } else {
        const error = await res.json();
        toast.error(error.error || 'Delete failed');
      }
    } catch (err) {
      toast.error('Error deleting admin');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      // Get actor info from localStorage
      const userStr = localStorage.getItem('user');
      const role = localStorage.getItem('role');
      let actorInfo = { actorId: 0, actorRole: role || 'SUPERADMIN', actorEmail: null };
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          actorInfo = {
            actorId: user.id || 0,
            actorRole: role || 'SUPERADMIN',
            actorEmail: user.email || null,
          };
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }

      const res = await fetch(`${API_URL}/admin/${id}/toggle-status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(actorInfo),
      });

      if (res.ok) {
        toast.success('Status updated!');
        fetchAdmins();
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const openEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      full_name: admin.full_name,
      email: admin.email,
      password: '',
      mobile_no: admin.mobile_no || '',
      role: admin.role,
    });
    setShowModal(true);
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setLogFilters({
      userType: '',
      action: '',
      category: '',
      sevakCode: '',
      fromDate: '',
      toDate: '',
      page: 1,
    });
  };

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case 'AUTH': return 'bg-blue-100 text-blue-800';
      case 'USER_MANAGEMENT': return 'bg-purple-100 text-purple-800';
      case 'SELF_EDIT': return 'bg-green-100 text-green-800';
      case 'ATTENDANCE': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
    if (action.includes('CREATE')) return 'bg-green-100 text-green-800';
    if (action.includes('EDIT')) return 'bg-yellow-100 text-yellow-800';
    if (action.includes('DELETE')) return 'bg-red-100 text-red-800';
    if (action.includes('ATTENDANCE')) return 'bg-indigo-100 text-indigo-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <HeaderLayout title="Control Panel">
      <ToastContainer />

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['admins', 'logs', 'stats', 'mudat', 'sales'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'admins' ? 'Manage Admins' : tab === 'logs' ? 'Activity Logs' : tab === 'stats' ? 'Statistics' : tab === 'mudat' ? 'Mudat Stats' : 'Sales'}
            </button>
          ))}
        </nav>
      </div>

      {/* Admins Tab */}
      {activeTab === 'admins' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Admin & Editor Users</h2>
            <button
              onClick={() => {
                setEditingAdmin(null);
                setFormData({
                  full_name: '',
                  email: '',
                  password: '',
                  mobile_no: '',
                  role: 'ADMIN',
                });
                setShowModal(true);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              + Add New
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => (
                  <tr key={admin.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{admin.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{admin.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{admin.mobile_no || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          admin.role === 'SUPERADMIN'
                            ? 'bg-red-100 text-red-800'
                            : admin.role === 'ADMIN'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {admin.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap space-x-2">
                      {admin.role !== 'SUPERADMIN' && (
                        <>
                          <button
                            onClick={() => openEditModal(admin)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleStatus(admin.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            {admin.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(admin.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">User Type</label>
                <select
                  value={logFilters.userType}
                  onChange={(e) => setLogFilters({ ...logFilters, userType: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Types</option>
                  <option value="SUPERADMIN">SUPERADMIN</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="EDITOR">EDITOR</option>
                  <option value="USER">USER</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                <select
                  value={logFilters.category}
                  onChange={(e) => setLogFilters({ ...logFilters, category: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
                <select
                  value={logFilters.action}
                  onChange={(e) => setLogFilters({ ...logFilters, action: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Actions</option>
                  {actions.map((act) => (
                    <option key={act} value={act}>{act}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Sevak Code</label>
                <input
                  type="text"
                  placeholder="Search Sevak..."
                  value={logFilters.sevakCode}
                  onChange={(e) => setLogFilters({ ...logFilters, sevakCode: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                <input
                  type="date"
                  value={logFilters.fromDate}
                  onChange={(e) => setLogFilters({ ...logFilters, fromDate: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
                <input
                  type="date"
                  value={logFilters.toDate}
                  onChange={(e) => setLogFilters({ ...logFilters, toDate: e.target.value, page: 1 })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
            </div>

            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sevak Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          log.user_type === 'SUPERADMIN'
                            ? 'bg-red-100 text-red-800'
                            : log.user_type === 'ADMIN'
                            ? 'bg-blue-100 text-blue-800'
                            : log.user_type === 'EDITOR'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {log.user_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.category && (
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getCategoryBadgeColor(log.category)}`}>
                          {log.category}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getActionBadgeColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">
                      {log.target_sevak_code || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={log.description}>
                      {log.description}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(log.old_value || log.new_value) && (
                        <button
                          onClick={() => openLogDetail(log)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Changes
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {logsPagination.totalPages > 1 && (
              <div className="px-6 py-3 flex justify-between items-center border-t bg-gray-50">
                <span className="text-sm text-gray-500">
                  Page {logsPagination.page} of {logsPagination.totalPages} ({logsPagination.total} total)
                </span>
                <div className="space-x-2">
                  <button
                    onClick={() => setLogFilters({ ...logFilters, page: logFilters.page - 1 })}
                    disabled={logFilters.page === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setLogFilters({ ...logFilters, page: logFilters.page + 1 })}
                    disabled={logFilters.page === logsPagination.totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total Logs" value={stats.totalLogs?.[0]?.count || 0} color="blue" />
            <StatCard title="Today's Activity" value={stats.todayLogs?.[0]?.count || 0} color="green" />
            <StatCard 
              title="User Edits Today" 
              value={stats.byAction?.find(a => a.action === 'EDIT_USER')?.count || 0} 
              color="yellow" 
            />
            <StatCard 
              title="Attendance Marked" 
              value={stats.byAction?.find(a => a.action === 'MARK_ATTENDANCE')?.count || 0} 
              color="purple" 
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By User Type */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Activity by User Type</h3>
              <div className="space-y-3">
                {stats.byUserType?.map((item) => (
                  <div key={item.user_type} className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      item.user_type === 'SUPERADMIN' ? 'bg-red-100 text-red-800' :
                      item.user_type === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                      item.user_type === 'EDITOR' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>{item.user_type}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Category */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Activity by Category</h3>
              <div className="space-y-3">
                {stats.byCategory?.map((item) => (
                  <div key={item.category} className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${getCategoryBadgeColor(item.category)}`}>
                      {item.category}
                    </span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Edits */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Recent User Edits</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {stats.recentEdits?.map((edit, i) => (
                  <div key={i} className="border-b pb-2">
                    <div className="flex justify-between text-sm">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${getActionBadgeColor(edit.action)}`}>
                        {edit.action}
                      </span>
                      <span className="text-gray-400 text-xs">
                        {new Date(edit.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 truncate">{edit.description}</p>
                    {edit.target_sevak_code && (
                      <p className="text-xs text-gray-400">Sevak: {edit.target_sevak_code}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Attendance */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Recent Attendance</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {stats.recentAttendance?.map((att, i) => (
                  <div key={i} className="border-b pb-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{att.target_sevak_code || 'Unknown'}</span>
                      <span className="text-gray-400 text-xs">
                        {new Date(att.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{att.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Logins */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Recent Logins</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email/Mobile</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Sevak Code</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.recentLogins?.map((login, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          login.user_type === 'SUPERADMIN' ? 'bg-red-100 text-red-800' :
                          login.user_type === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                          login.user_type === 'EDITOR' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{login.user_type}</span>
                      </td>
                      <td className="px-4 py-2 text-sm">{login.user_email || login.user_mobile || '-'}</td>
                      <td className="px-4 py-2 text-sm font-mono">{login.target_sevak_code || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{new Date(login.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Mudat Stats Tab */}
      {activeTab === 'mudat' && (
        <div className="space-y-6">
          <h2 className="text-xl font-bold mb-4">Sunday Mudat Statistics</h2>
          
          {/* Coming Sunday Highlight */}
          <div className="bg-orange-100 border-l-4 border-orange-500 p-4 rounded">
            <div className="flex items-center">
              <span className="text-orange-700 font-semibold text-lg">
                Coming Sunday: {new Date(getComingSunday()).toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            </div>
          </div>

          {/* Past Sundays Stats */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Past Sundays Attendance</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Sevaks</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Morning</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Evening</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">District Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {mudatStats.past?.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No Sunday mudat data found
                      </td>
                    </tr>
                  )}
                  {mudatStats.past?.map((stat, i) => (
                    <tr key={i} className={i === 0 ? 'bg-green-50' : ''}>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-medium">
                          {new Date(stat.mudat_date).toLocaleDateString('en-IN', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        {i === 0 && <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded">Latest</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-lg font-bold text-purple-700">{stat.total_present}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-blue-600 font-medium">{stat.morning_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-green-600 font-medium">{stat.evening_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => fetchMudatDetail(stat.mudat_date)}
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* District Detail Modal */}
          {selectedMudatDate && (
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  District Detail - {new Date(selectedMudatDate).toLocaleDateString('en-IN')}
                </h3>
                <button
                  onClick={() => setSelectedMudatDate(null)}
                  className="text-gray-500 hover:text-red-500"
                >
                  ✕
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">District</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">State</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Present</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Total Sevaks</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {mudatDetail.map((d, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm font-medium">{d.district}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{d.state}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="text-green-600 font-bold">{d.present_count}</span>
                        </td>
                        <td className="px-4 py-2 text-center text-gray-500">{d.total_in_district}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
                            {d.mudat_days || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          <h2 className="text-xl font-bold">Sales Management</h2>

          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
              <input
                type="date"
                value={salesDateFilter}
                onChange={(e) => {
                  setSalesDateFilter(e.target.value);
                  fetchSalesData(e.target.value, salesPayFilter);
                }}
                className="px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Payment Mode</label>
              <select
                value={salesPayFilter}
                onChange={(e) => {
                  setSalesPayFilter(e.target.value);
                  fetchSalesData(salesDateFilter, e.target.value);
                }}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All</option>
                <option value="cash">Cash</option>
                <option value="online">Online</option>
              </select>
            </div>
            <button
              onClick={() => fetchSalesData(salesDateFilter, salesPayFilter)}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
            >
              🔄 Load
            </button>
          </div>

          {/* Summary Cards */}
          {salesSummary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-red-700">₹{salesSummary.total_revenue}</p>
                <p className="text-xs text-gray-400">{salesSummary.total_bills} bills</p>
              </div>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="text-xs text-gray-500">💵 Cash</p>
                <p className="text-2xl font-bold text-green-700">₹{salesSummary.cash_revenue}</p>
                <p className="text-xs text-gray-400">{salesSummary.cash_count} bills</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-xs text-gray-500">📱 Online</p>
                <p className="text-2xl font-bold text-blue-700">₹{salesSummary.online_revenue}</p>
                <p className="text-xs text-gray-400">{salesSummary.online_count} bills</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <p className="text-xs text-gray-500">🎁 Combo Qty</p>
                <p className="text-2xl font-bold text-purple-700">{salesSummary.combo_qty}</p>
                <p className="text-xs text-gray-400">₹{salesSummary.combo_revenue}</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <p className="text-xs text-gray-500">📦 Single Qty</p>
                <p className="text-2xl font-bold text-orange-700">{salesSummary.single_qty}</p>
                <p className="text-xs text-gray-400">₹{salesSummary.single_revenue}</p>
              </div>
            </div>
          )}

          {/* By User Breakdown */}
          {salesByUser.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-orange-500 px-4 py-3">
                <h3 className="text-white font-semibold">Sales by User</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Salesperson</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Bills</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">💵 Cash</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">📱 Online</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesByUser.map((u, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{u.sold_by_name}</td>
                      <td className="px-4 py-3 text-center">{u.bills}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-700">₹{u.total}</td>
                      <td className="px-4 py-3 text-right text-green-700">
                        ₹{u.cash_total} <span className="text-xs text-gray-400">({u.cash_bills})</span>
                      </td>
                      <td className="px-4 py-3 text-right text-blue-700">
                        ₹{u.online_total} <span className="text-xs text-gray-400">({u.online_bills})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Full Sales Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-red-600 px-4 py-3">
              <h3 className="text-white font-semibold">All Sales ({salesData.length})</h3>
            </div>
            {salesLoading ? (
              <div className="py-10 text-center text-gray-400">Loading...</div>
            ) : salesData.length === 0 ? (
              <div className="py-10 text-center text-gray-400">No sales found. Click Load to fetch data.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bill No</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sold By</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {salesData.map((sale, i) => (
                      <tr key={sale.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{sale.bill_no}</td>
                        <td className="px-3 py-2 font-medium">
                          {sale.product_type === 'combo' ? '🎁 Combo' : '📦 Single'}
                        </td>
                        <td className="px-3 py-2 text-center">{sale.quantity}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-700">₹{sale.total_amount}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            sale.payment_mode === 'cash'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {sale.payment_mode === 'cash' ? '💵 Cash' : '📱 Online'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{sale.sold_by_name}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{sale.customer_name || '—'}</td>
                        <td className="px-3 py-2 text-center text-xs text-gray-500">
                          {new Date(sale.created_at).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short',
                            hour: '2-digit', minute: '2-digit', hour12: true,
                          })}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleDeleteSale(sale.id)}
                            disabled={salesDeletingId === sale.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded disabled:opacity-40"
                          >
                            {salesDeletingId === sale.id ? '⏳' : '🗑️'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-red-50 border-t-2 border-red-200">
                      <td colSpan={3} className="px-3 py-3 font-bold text-gray-700">
                        Total ({salesData.length} bills)
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-red-700 text-base">
                        ₹{salesData.reduce((s, x) => s + Number(x.total_amount), 0)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingAdmin ? 'Edit Admin' : 'Add New Admin'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Password {editingAdmin && '(leave empty to keep current)'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingAdmin}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile No</label>
                <input
                  type="text"
                  value={formData.mobile_no}
                  onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="ADMIN">ADMIN</option>
                  <option value="EDITOR">EDITOR</option>
                  <option value="SALES">SALES</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Activity Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">User Type</label>
                  <p className="font-medium">{selectedLog.user_type}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Action</label>
                  <p className="font-medium">{selectedLog.action}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Category</label>
                  <p className="font-medium">{selectedLog.category || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Sevak Code</label>
                  <p className="font-medium font-mono">{selectedLog.target_sevak_code || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">User Email/Mobile</label>
                  <p className="font-medium">{selectedLog.user_email || selectedLog.user_mobile || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Time</label>
                  <p className="font-medium">{new Date(selectedLog.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500">Description</label>
                <p className="font-medium">{selectedLog.description}</p>
              </div>

              {/* Changes */}
              {(selectedLog.old_value || selectedLog.new_value) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Field Changes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Old Values */}
                    {selectedLog.old_value && (
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-red-800 mb-2">Previous Values</h5>
                        <div className="space-y-2">
                          {Object.entries(selectedLog.old_value).map(([key, value]) => (
                            <div key={key} className="text-sm">
                              <span className="text-gray-500">{getFieldLabel(key)}:</span>{' '}
                              <span className="text-red-700">{formatFieldValue(key, value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New Values */}
                    {selectedLog.new_value && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-green-800 mb-2">New Values</h5>
                        <div className="space-y-2">
                          {Object.entries(selectedLog.new_value).map(([key, value]) => (
                            <div key={key} className="text-sm">
                              <span className="text-gray-500">{getFieldLabel(key)}:</span>{' '}
                              <span className="text-green-700">{formatFieldValue(key, value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </HeaderLayout>
  );
};

const StatCard = ({ title, value, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    purple: 'bg-purple-50 border-purple-200',
  };

  return (
    <div className={`p-6 rounded-lg border ${colors[color]}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
    </div>
  );
};

export default ControlPanel;